import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { toast } from 'sonner';
import type {
  SendTestEmailRequest,
  SendTestEmailResponse,
  TestEmailEnvironment,
  TestEmailProduct,
  TestEmailTemplate,
} from '@metron/shared';
import {
  createLogger,
  TEST_EMAIL_ENVIRONMENTS,
  TEST_EMAIL_PRODUCTS,
  TEST_EMAIL_TEMPLATE_LABELS,
  TEST_EMAIL_TEMPLATES,
} from '@metron/shared';

const logger = createLogger('SendTestEmailDialog');

/** Narrow a Select value to one of the options it was built from */
function oneOf<T extends string>(options: readonly T[], value: string): value is T {
  return (options as readonly string[]).includes(value);
}

interface SendTestEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSendTestEmail: (request: SendTestEmailRequest) => Promise<SendTestEmailResponse>;
  /** Pre-filled recipient: the signed-in user's email */
  defaultRecipient?: string;
}

/**
 * Preview or send a Cognito email template (onboarding invite, password reset,
 * ...) as a `[TEST]` email. Credentials in it are placeholders, and the
 * sender only delivers to internal addresses. The recipient field shows the
 * signed-in user; left as is or blank, the API sends to the signed-in user.
 */
export function SendTestEmailDialog(
  { open, onOpenChange, onSendTestEmail, defaultRecipient = '' }: SendTestEmailDialogProps,
) {
  const [template, setTemplate] = useState<TestEmailTemplate>('admin-invite');
  const [environment, setEnvironment] = useState<TestEmailEnvironment>('production');
  const [product, setProduct] = useState<TestEmailProduct>('plus');
  const [to, setTo] = useState(defaultRecipient);
  const recipient = to.trim();
  const [preview, setPreview] = useState<SendTestEmailResponse | null>(null);
  const [pending, setPending] = useState<'preview' | 'send' | null>(null);
  // Bumped by every request and every option change, so a response that comes
  // back after the options changed (or after a newer request) is ignored
  const requestId = useRef(0);
  // The request that owns `pending`: bumped only by a new request or a reopen
  // (not by option changes), so an older request settling can't re-enable the
  // buttons while a newer one is running
  const inFlight = useRef(0);

  // The dialog stays mounted between opens, so start each open from the
  // signed-in user, no preview and nothing in flight (a request still running
  // from the last open is ignored through requestId). Only on opening: a
  // prefill that changes while open must not overwrite what was typed.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setTo(defaultRecipient);
      requestId.current++;
      inFlight.current++;
      setPreview(null);
      setPending(null);
    }
    wasOpen.current = open;
  }, [open, defaultRecipient]);

  const request = (dryRun: boolean): SendTestEmailRequest => ({
    template,
    environment,
    product,
    dryRun,
    // Only send a recipient the admin entered. Left blank or as the prefill,
    // the API looks up the signed-in user's email itself, so it never relies
    // on the frontend's copy
    ...(recipient && recipient !== defaultRecipient.trim() ? { to: recipient } : {}),
  });

  const run = async (dryRun: boolean) => {
    const id = ++requestId.current;
    const flight = ++inFlight.current;
    setPending(dryRun ? 'preview' : 'send');
    try {
      const result = await onSendTestEmail(request(dryRun));
      if (id === requestId.current) {
        setPreview(result);
      }
      if (!dryRun) {
        if (result.sent) {
          toast.success(`Test email sent to ${result.to}`);
        } else {
          toast.warning('The test email was rendered but not sent');
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send test email';
      toast.error(message);
      logger.error(
        { error },
        dryRun ? 'Failed to preview test email' : 'Failed to send test email',
      );
    } finally {
      if (flight === inFlight.current) {
        setPending(null);
      }
    }
  };

  // A preview only matches the options it was rendered with
  const clearPreview = () => {
    requestId.current++;
    setPreview(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[720px] max-h-[90vh] flex flex-col overflow-hidden'>
        <DialogHeader>
          <DialogTitle>Send Test Email</DialogTitle>
          <DialogDescription>
            Send one of the account emails (onboarding invite, password reset, ...) as it would go
            to a user, with placeholder credentials and a [TEST] subject. Only internal addresses
            can receive test emails.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-4 py-2 sm:grid-cols-2 overflow-y-auto'>
          <div className='grid gap-2 sm:col-span-2'>
            <Label htmlFor='test-email-template'>Email</Label>
            <Select
              value={template}
              onValueChange={(value) => {
                if (oneOf(TEST_EMAIL_TEMPLATES, value)) {
                  setTemplate(value);
                  clearPreview();
                }
              }}
            >
              <SelectTrigger id='test-email-template' aria-label='Email'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEST_EMAIL_TEMPLATES.map((name) => (
                  <SelectItem key={name} value={name}>
                    {TEST_EMAIL_TEMPLATE_LABELS[name]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='grid gap-2'>
            <Label htmlFor='test-email-product'>Product</Label>
            <Select
              value={product}
              onValueChange={(value) => {
                if (oneOf(TEST_EMAIL_PRODUCTS, value)) {
                  setProduct(value);
                  clearPreview();
                }
              }}
            >
              <SelectTrigger id='test-email-product' aria-label='Product'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='plus'>WaterScope Plus</SelectItem>
                <SelectItem value='my-waterscope'>My WaterScope</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className='grid gap-2'>
            <Label htmlFor='test-email-environment'>Environment</Label>
            <Select
              value={environment}
              onValueChange={(value) => {
                if (oneOf(TEST_EMAIL_ENVIRONMENTS, value)) {
                  setEnvironment(value);
                  clearPreview();
                }
              }}
            >
              <SelectTrigger id='test-email-environment' aria-label='Environment'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='production'>Production</SelectItem>
                <SelectItem value='development'>Development</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className='grid gap-2 sm:col-span-2'>
            <Label htmlFor='test-email-to'>Recipient</Label>
            <Input
              id='test-email-to'
              type='email'
              placeholder='Leave blank to send to yourself'
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
                clearPreview();
              }}
            />
          </div>

          {preview && (
            <div className='grid gap-2 sm:col-span-2'>
              <p className='text-sm'>
                <span className='text-muted-foreground'>Subject:</span> {preview.subject}
              </p>
              {/* Sandboxed: the email's HTML can't run scripts or reach this page */}
              <iframe
                title='Email preview'
                sandbox=''
                srcDoc={preview.htmlContent}
                className='h-80 w-full rounded-md border bg-white'
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => run(true)} disabled={pending !== null}>
            {pending === 'preview' ? 'Rendering...' : 'Preview'}
          </Button>
          <Button onClick={() => run(false)} disabled={pending !== null}>
            {pending === 'send' ? 'Sending...' : 'Send Test Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
